import { useState, useEffect } from 'react'
import { useAdminUser, useUserFeatures, useGrantFeatures, useRevokeFeature } from '@/hooks/useAdminUsers'
import FeatureToggle, { FeatureGroup } from './FeatureToggle'

interface UserDetailDrawerProps {
  userId: string | null
  onClose: () => void
  onImpersonate?: (tenantId: string) => void
}

export default function UserDetailDrawer({ userId, onClose, onImpersonate }: UserDetailDrawerProps) {
  const [isVisible, setIsVisible] = useState(false)

  const { data: user, isLoading: userLoading } = useAdminUser(userId)
  const { data: features, isLoading: featuresLoading } = useUserFeatures(userId)
  const grantMutation = useGrantFeatures()
  const revokeMutation = useRevokeFeature()

  // Animate in
  useEffect(() => {
    if (userId) {
      requestAnimationFrame(() => setIsVisible(true))
    } else {
      setIsVisible(false)
    }
  }, [userId])

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(onClose, 200)
  }

  const handleFeatureToggle = async (featureKey: string, enabled: boolean) => {
    if (!userId) return

    if (enabled) {
      await grantMutation.mutateAsync({ userId, features: [featureKey] })
    } else {
      await revokeMutation.mutateAsync({ userId, featureKey })
    }
  }

  // Group features by category
  const featuresByCategory = features?.reduce((acc, feature) => {
    const category = feature.category
    if (!acc[category]) acc[category] = []
    acc[category].push(feature)
    return acc
  }, {} as Record<string, typeof features>) || {}

  const categoryTitles: Record<string, string> = {
    product: 'Products',
    portal: 'Portal Features',
    admin: 'Admin Features',
  }

  const isLoading = userLoading || featuresLoading

  if (!userId) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-200 ${
          isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleClose}
      />

      {/* Drawer */}
      <div
        className={`fixed inset-y-0 right-0 w-full max-w-md bg-[#0a0f14] border-l border-[#2a3542] shadow-xl z-50 transition-transform duration-200 ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2a3542]">
          <h2 className="text-lg font-semibold text-[#e8eaed] font-mono">User Details</h2>
          <button
            onClick={handleClose}
            className="p-2 text-[#8b949e] hover:text-[#e8eaed] hover:bg-[#1c2630] rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : user ? (
            <>
              {/* User Info */}
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-teal-500 to-blue-600 flex items-center justify-center text-white text-xl font-semibold">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-[#e8eaed]">{user.name}</h3>
                    <p className="text-sm text-[#8b949e]">{user.email}</p>
                  </div>
                </div>

                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-[#8b949e]">Role</span>
                    <p className="text-[#e8eaed] capitalize">
                      {user.role === 'superuser' ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="w-2 h-2 rounded-full bg-purple-500" />
                          Superuser
                        </span>
                      ) : user.role === 'employee' ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="w-2 h-2 rounded-full bg-blue-500" />
                          Employee
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <span className="w-2 h-2 rounded-full bg-teal-500" />
                          Tenant Admin
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <span className="text-[#8b949e]">Status</span>
                    <p className={user.is_active ? 'text-green-400' : 'text-red-400'}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                  {user.tenant_name && (
                    <div className="col-span-2">
                      <span className="text-[#8b949e]">Tenant</span>
                      <p className="text-[#e8eaed]">{user.tenant_name}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-[#8b949e]">MFA</span>
                    <p className={user.mfa_enabled ? 'text-green-400' : 'text-yellow-400'}>
                      {user.mfa_enabled ? 'Enabled' : 'Disabled'}
                    </p>
                  </div>
                  <div>
                    <span className="text-[#8b949e]">Last Login</span>
                    <p className="text-[#e8eaed]">
                      {user.last_login
                        ? new Date(user.last_login).toLocaleDateString()
                        : 'Never'}
                    </p>
                  </div>
                </div>

                {/* Impersonate Button (for tenant users) */}
                {user.tenant_id && onImpersonate && (
                  <button
                    onClick={() => onImpersonate(user.tenant_id!)}
                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    Impersonate Tenant
                  </button>
                )}
              </div>

              {/* Feature Toggles */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-[#e8eaed] uppercase tracking-wider">
                  Feature Access
                </h3>

                {Object.entries(featuresByCategory).map(([category, categoryFeatures]) => (
                  <FeatureGroup key={category} title={categoryTitles[category] || category}>
                    {categoryFeatures?.map((feature) => (
                      <FeatureToggle
                        key={feature.key}
                        featureKey={feature.key}
                        name={feature.name}
                        description={feature.description}
                        hasAccess={feature.has_access}
                        source={feature.source}
                        onToggle={handleFeatureToggle}
                        disabled={grantMutation.isPending || revokeMutation.isPending}
                      />
                    ))}
                  </FeatureGroup>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center text-[#8b949e] py-12">
              User not found
            </div>
          )}
        </div>
      </div>
    </>
  )
}
